import { Injectable } from '@angular/core';

import { ITokenPayload } from '@azavista/planner-lib';
import { IAclObject } from '@azavista/components/shared';

@Injectable({
    providedIn: 'root'
})
export class AclService {
    private tokenPayload: ITokenPayload;

    setTokenPayload(tokenPayload: ITokenPayload): void {
        this.tokenPayload = tokenPayload;
    }

    getCurrentUserTeamIdsContainingScope(scope: string): number[] {
        const teamIds = this.getTeamIdsFromToken();
        const result: number[] = [];
        for (const teamId of teamIds) {
            const teamScopes = this.tokenPayload.s[teamId];
            if (teamScopes.indexOf(scope) >= 0) {
                result.push(teamId);
            }
        }
        return result;
    }

    getCurrentUserTeamIdsContainingAnyScope(scopes: string[]): number[] {
        const teamIds = this.getTeamIdsFromToken();
        const result: number[] = [];
        for (const teamId of teamIds) {
            const teamScopes = this.tokenPayload.s[teamId];
            for (const scope of scopes) {
                if (teamScopes.indexOf(scope) >= 0) {
                    if (result.indexOf(teamId) === -1) {
                        result.push(teamId);
                        break;
                    }
                }
            }
        }
        return result;
    }

    getCurrentUserId(): number {
        return this.tokenPayload.u;
    }

    getTeamsAndScopesFromToken(): { [key: number]: string[] } {
        return this.tokenPayload.s;
    }

    isAdmin(): boolean {
        if (!this.tokenPayload) {
            return false;
        }
        return this.tokenPayload.g;
    }

    getAclObjectForCurrentUser(requiredScopes?: string[]): IAclObject {
        const teamIds = this.getTeamIdsFromToken();
        return this.getAclObject(teamIds, requiredScopes);
    }

    getDefaultDenyAclObject(): IAclObject {
        const obj: IAclObject = {
            allowed: false, availableScopes: [], requiredScopes: [], isAdmin: false
        };
        return obj;
    }

    getAclObject(teamIds: number[], requiredScopes?: string[]): IAclObject {
        if (!this.tokenPayload) {
            // Token is not provided - return everything-disabled type of object
            return { availableScopes: [], requiredScopes: requiredScopes || [], isAdmin: false, allowed: false };
        }
        const isAdmin = this.tokenPayload.g;
        if (isAdmin) {
            return { availableScopes: [], requiredScopes: [], isAdmin: true, allowed: true };
        }
        if (!requiredScopes) {
            // Required scopes are not provided - return object with properties set to null
            // This is an indication that everything should be allowed
            return { availableScopes: [], requiredScopes: [], isAdmin: this.tokenPayload.g, allowed: true };
        }
        teamIds = teamIds || this.getTeamIdsFromToken();
        const availableScopes = this.getAvailableScopes(teamIds);
        const result: IAclObject = {
            availableScopes: availableScopes,
            requiredScopes: requiredScopes,
            isAdmin: isAdmin,
            allowed: isAdmin || this.hasRequiredScope(availableScopes, requiredScopes)
        };
        return result;
    }

    getTeamIdsFromToken(): number[] {
        const teamsWithScopes = this.tokenPayload.s;
        const teamIds = Object.keys(teamsWithScopes).map(teamId => +teamId);
        return teamIds;
    }

    hasRequiredScope(availableScopes: string[], requiredScopes: string[]): boolean {
        if (!availableScopes || !requiredScopes) {
            return false;
        }
        for (const availableScope of availableScopes) {
            if (requiredScopes.indexOf(availableScope) >= 0) {
                // We found at least one required scope in available scopes
                return true;
            }
        }
        return false;
    }

    getAvailableScopes(teamIds: number[]): string[] {
        const teamScopes = this.getTeamsScopesArray(teamIds);
        return this.createDistinctSet(teamScopes);
    }

    getTeamsScopesArray(teamIds: number[]): Array<string[]> {
        const result: Array<string[]> = [];
        const teamsWithScopes = this.tokenPayload.s;
        for (const teamId of teamIds) {
            const teamScopes = teamsWithScopes[teamId];
            if (teamScopes && teamScopes.length) {
                result.push(teamScopes);
            }
        }
        return result;
    }

    createDistinctSet(arrays: Array<string[]>): string[] {
        const result: string[] = [];
        const mapObj: { [key: string]: boolean } = {};
        for (const arr of arrays) {
            for (const item of arr) {
                this.addIfNotExists(item, result, mapObj);
            }
        }
        return result;
    }

    addIfNotExists(item: string, arr: string[], mapObj: { [key: string]: boolean }): boolean {
        if (!mapObj[item]) {
            arr.push(item);
            mapObj[item] = true;
            return true;
        }
        return false;
    }
}
